import { POSAdapter } from '../POSAdapter';
import {
  POSConfig,
  POSTable,
  POSOrder,
  POSItem,
  POSSyncResponse,
  POSPaymentUpdate
} from '../types';

/**
 * Mock POS adapter - connects to your local Mock POS API
 */
export class MockPOSAdapter extends POSAdapter {
  private apiUrl: string;

  constructor(config: POSConfig) {
    super(config);
    
    // Get API URL from credentials or use default
    this.apiUrl = config.credentials?.apiUrl || 'http://localhost:3001';
    
    console.log('🔧 MockPOSAdapter initialized with API:', this.apiUrl);
  }

  async testConnection(): Promise<boolean> {
    try {
      const response = await fetch(`${this.apiUrl}/api/restaurant`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      
      console.log('✅ Mock POS: Connection successful');
      return true;
    } catch (error) {
      console.error('❌ Mock POS: Connection failed', error);
      return false;
    }
  }

  async getTables(): Promise<POSTable[]> {
    try {
      const response = await fetch(`${this.apiUrl}/api/tables`);
      const data = await response.json();
      
      return data.map((table: any) => ({
        id: String(table.id),
        name: String(table.table_number),
        status: table.status === 'occupied' ? 'occupied' : 'available',
        section: table.section
      }));
    } catch (error) {
      console.error('Error fetching tables:', error);
      return [];
    }
  }

  async getTable(posTableId: string): Promise<POSTable | null> {
    try {
      const response = await fetch(`${this.apiUrl}/api/tables/${posTableId}`);
      if (!response.ok) return null;
      
      const table = await response.json();
      
      return {
        id: String(table.id),
        name: String(table.table_number),
        status: table.status === 'occupied' ? 'occupied' : 'available',
        section: table.section
      };
    } catch (error) {
      console.error('Error fetching table:', error);
      return null;
    }
  }

  async getTableOrder(posTableId: string): Promise<POSOrder | null> {
    try {
      const response = await fetch(`${this.apiUrl}/api/tables/${posTableId}/bill`);
      if (!response.ok) return null;
      
      const { bill } = await response.json();
      if (!bill) return null;
      
      return {
        id: String(bill.id),
        tableId: posTableId,
        items: bill.items.map((item: any) => ({
          id: String(item.id),
          name: item.name,
          price: Number(item.price),
          quantity: item.quantity,
          category: item.category || '',
          paid: false
        })),
        subtotal: Number(bill.subtotal),
        tax: Number(bill.tax),
        total: Number(bill.total),
        status: bill.status === 'paid' ? 'closed' : 'open',
        createdAt: new Date(bill.created_at),
        updatedAt: new Date(bill.updated_at)
      };
    } catch (error) {
      console.error('Error fetching order:', error);
      return null;
    }
  }

  async getOpenOrders(): Promise<POSOrder[]> {
    try {
      const response = await fetch(`${this.apiUrl}/api/bills?status=open`);
      const bills = await response.json();
      
      return bills.map((bill: any) => ({
        id: String(bill.id),
        tableId: String(bill.table_id),
        items: bill.items?.map((item: any) => ({
          id: String(item.id),
          name: item.name,
          price: Number(item.price),
          quantity: item.quantity,
          category: '',
          paid: false
        })) || [],
        subtotal: Number(bill.subtotal),
        tax: Number(bill.tax),
        total: Number(bill.total),
        status: 'open',
        createdAt: new Date(bill.created_at),
        updatedAt: new Date(bill.updated_at)
      }));
    } catch (error) {
      console.error('Error fetching open orders:', error);
      return [];
    }
  }

  async sendPaymentUpdate(payment: POSPaymentUpdate): Promise<POSSyncResponse> {
    try {
      const response = await fetch(`${this.apiUrl}/api/bills/${payment.orderId}/mark-paid`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          paymentMethod: payment.paymentMethod,
          amountPaid: payment.amountPaid
        })
      });
      
      if (!response.ok) {
        return {
          success: false,
          error: `Failed to mark bill as paid: ${response.status}`,
          timestamp: new Date()
        };
      }
      
      return {
        success: true,
        data: { orderId: payment.orderId },
        timestamp: new Date()
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.message,
        timestamp: new Date()
      };
    }
  }

  async closeOrder(orderId: string): Promise<POSSyncResponse> {
    return this.sendPaymentUpdate({
      orderId,
      itemIds: [],
      amountPaid: 0,
      paymentMethod: 'external',
      timestamp: new Date()
    });
  }

  async syncTable(mesaId: string, posTableId: string): Promise<POSSyncResponse> {
    try {
      const table = await this.getTable(posTableId);
      if (!table) {
        return {
          success: false,
          error: `Table ${posTableId} not found`,
          timestamp: new Date()
        };
      }
      
      const order = await this.getTableOrder(posTableId);
      
      return {
        success: true,
        data: { table, order, mesaId },
        timestamp: new Date()
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.message,
        timestamp: new Date()
      };
    }
  }
}
